import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

export interface DateRangePickerProps {
    startDate?: string; // YYYY-MM-DD
    endDate?: string;   // YYYY-MM-DD
    onChange: (startDate: string, endDate: string) => void;
    onClear?: () => void;
    placeholder?: string;
    className?: string;
    buttonClassName?: string;
    align?: 'left' | 'right';
    allowFutureDates?: boolean;
    maxDate?: Date;
    minDate?: Date;
    accentColor?: string; // Default: '#4a7c59' (Forest Green)
}

const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Date parsing and formatting helpers
const parseISODate = (iso?: string | null): Date | null => {
    if (!iso) return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m, d);
};

const toISODate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const formatDateToDdmmyyyy = (dateString?: string | null): string => {
    if (!dateString) return '';
    try {
        const parts = dateString.split('-');
        if (parts.length === 3) {
            return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
        }
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch {
        return dateString;
    }
};

const isSameDay = (a: Date | null, b: Date | null): boolean => {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
};

const buildCalendarGrid = (viewDate: Date): (Date | null)[] => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const grid: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) grid.push(null);
    for (let d = 1; d <= totalDays; d++) grid.push(new Date(year, month, d));
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate = '',
    endDate = '',
    onChange,
    onClear,
    placeholder = 'Select Dates',
    className = '',
    buttonClassName = '',
    align = 'right',
    allowFutureDates = false,
    maxDate,
    minDate,
    accentColor = '#4a7c59'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // Most triggers aren't pinned to the screen's right edge — on mobile,
    // filters often stack as full-width rows with a content-width button, so
    // the fixed `align="right"` default can run the 275px panel off the LEFT
    // edge instead. Re-measure after opening and flip once if so (deliberately
    // not resetting to `align` first — see the identical fix in Select.tsx).
    const [computedAlign, setComputedAlign] = useState(align);

    const today = useMemo(() => {
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        return now;
    }, []);

    const effectiveMaxDate = useMemo(() => {
        if (maxDate) return maxDate;
        if (!allowFutureDates) return today;
        return undefined;
    }, [maxDate, allowFutureDates, today]);

    const [calMonth, setCalMonth] = useState<Date>(() => {
        const s = parseISODate(startDate);
        return s || new Date();
    });

    const [stagedStart, setStagedStart] = useState<Date | null>(null);
    const [stagedEnd, setStagedEnd] = useState<Date | null>(null);

    // Sync external props when opened
    const handleOpen = () => {
        const s = parseISODate(startDate);
        const e = parseISODate(endDate);
        setStagedStart(s);
        setStagedEnd(e);
        setCalMonth(s || new Date());
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    useLayoutEffect(() => {
        if (!isOpen || !panelRef.current) return;
        const rect = panelRef.current.getBoundingClientRect();
        if (computedAlign === 'right' && rect.left < 0) {
            setComputedAlign('left');
        } else if (computedAlign === 'left' && rect.right > window.innerWidth) {
            setComputedAlign('right');
        }
    }, [isOpen, computedAlign]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const calendarGrid = useMemo(() => buildCalendarGrid(calMonth), [calMonth]);

    const canGoNextMonth = useMemo(() => {
        if (!effectiveMaxDate) return true;
        const nextMonthStart = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1);
        return nextMonthStart <= effectiveMaxDate;
    }, [calMonth, effectiveMaxDate]);

    const canGoPrevMonth = useMemo(() => {
        if (!minDate) return true;
        const prevMonthEnd = new Date(calMonth.getFullYear(), calMonth.getMonth(), 0);
        return prevMonthEnd >= minDate;
    }, [calMonth, minDate]);

    const goToMonth = (year: number, month: number) => {
        const target = new Date(year, month, 1);
        if (effectiveMaxDate) {
            const maxMonth = new Date(effectiveMaxDate.getFullYear(), effectiveMaxDate.getMonth(), 1);
            if (target > maxMonth) {
                setCalMonth(maxMonth);
                return;
            }
        }
        if (minDate) {
            const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
            if (target < minMonth) {
                setCalMonth(minMonth);
                return;
            }
        }
        setCalMonth(target);
    };

    const calendarYearOptions = useMemo(() => {
        const currentYear = today.getFullYear();
        const startYear = currentYear - 30;
        const endYear = allowFutureDates ? currentYear + 5 : currentYear;
        const years: number[] = [];
        for (let y = endYear; y >= startYear; y--) {
            years.push(y);
        }
        return years;
    }, [today, allowFutureDates]);

    const handleDayClick = (day: Date) => {
        if (effectiveMaxDate && day > effectiveMaxDate) return;
        if (minDate && day < minDate) return;

        if (!stagedStart || (stagedStart && stagedEnd)) {
            setStagedStart(day);
            setStagedEnd(null);
        } else if (day < stagedStart) {
            setStagedEnd(stagedStart);
            setStagedStart(day);
        } else {
            setStagedEnd(day);
        }
    };

    const handleApply = () => {
        if (stagedStart) {
            const startISO = toISODate(stagedStart);
            const endISO = toISODate(stagedEnd || stagedStart);
            onChange(startISO, endISO);
        }
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setStagedStart(null);
        setStagedEnd(null);
        if (onClear) {
            onClear();
        } else {
            onChange('', '');
        }
        setIsOpen(false);
    };

    const displayText = useMemo(() => {
        if (isOpen && stagedStart) {
            if (stagedEnd) {
                return `${formatDateToDdmmyyyy(toISODate(stagedStart))} - ${formatDateToDdmmyyyy(toISODate(stagedEnd))}`;
            }
            return `${formatDateToDdmmyyyy(toISODate(stagedStart))} - ...`;
        }
        if (startDate && endDate) {
            return `${formatDateToDdmmyyyy(startDate)} - ${formatDateToDdmmyyyy(endDate)}`;
        }
        if (startDate) {
            return formatDateToDdmmyyyy(startDate);
        }
        return placeholder;
    }, [isOpen, stagedStart, stagedEnd, startDate, endDate, placeholder]);

    const hasValue = Boolean(startDate || endDate);

    return (
        <div className={twMerge('relative inline-block', className)} ref={containerRef}>
            <button
                type="button"
                onClick={() => (isOpen ? handleClose() : handleOpen())}
                className={twMerge('flex items-center justify-between gap-2 h-[42px] px-3 text-xs sm:text-sm border border-slate-200 shadow-xs rounded-2xl bg-white text-slate-700 outline-none hover:border-slate-300 focus:border-[#1B4332] transition-all duration-200 cursor-pointer font-semibold whitespace-nowrap', buttonClassName)}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <Calendar size={15} className="text-slate-400 group-hover:text-[var(--drp-accent)] transition-colors shrink-0" style={{ '--drp-accent': accentColor } as React.CSSProperties} />
                    <span className="truncate">{displayText}</span>
                </div>
                {hasValue ? (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={handleClear}
                        className="p-0.5 text-slate-400 hover:text-slate-600 rounded-full transition-colors cursor-pointer shrink-0 ml-1"
                        title="Clear dates"
                    >
                        <X size={13} />
                    </span>
                ) : (
                    <ChevronDown size={13} className={`text-slate-400 transition-transform shrink-0 ml-1 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>

            {isOpen && (
                <div
                    ref={panelRef}
                    className={`absolute top-full ${computedAlign === 'right' ? 'right-0' : 'left-0'} mt-2 w-[275px] bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-slate-100 z-50 p-3 overflow-hidden font-sans`}
                >
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between gap-1 mb-2">
                        <button
                            type="button"
                            onClick={() => canGoPrevMonth && setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))}
                            disabled={!canGoPrevMonth}
                            className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="Previous Month"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center gap-1">
                            <select
                                value={calMonth.getMonth()}
                                onChange={(e) => goToMonth(calMonth.getFullYear(), Number(e.target.value))}
                                className="font-bold text-[#2e3230] text-xs tracking-tight bg-transparent outline-none cursor-pointer appearance-none text-center"
                            >
                                {MONTH_LABELS.map((label, idx) => {
                                    const monthStart = new Date(calMonth.getFullYear(), idx, 1);
                                    const isDisabled = effectiveMaxDate ? monthStart > effectiveMaxDate : false;
                                    return (
                                        <option key={label} value={idx} disabled={isDisabled}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                            <select
                                value={calMonth.getFullYear()}
                                onChange={(e) => goToMonth(Number(e.target.value), calMonth.getMonth())}
                                className="font-bold text-[#2e3230] text-xs tracking-tight bg-transparent outline-none cursor-pointer appearance-none text-center"
                            >
                                {calendarYearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => canGoNextMonth && setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))}
                            disabled={!canGoNextMonth}
                            className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            aria-label="Next Month"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    {/* Weekday Labels */}
                    <div className="grid grid-cols-7 gap-x-0 mb-1">
                        {WEEKDAY_LABELS.map((d) => (
                            <div key={d} className="text-[9px] font-black text-slate-600 text-center py-0.5">
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* Dates Grid — zero horizontal gap for smooth continuous band */}
                    <div className="grid grid-cols-7 gap-x-0 gap-y-0.5">
                        {calendarGrid.map((day, idx) => {
                            if (!day) return <div key={idx} className="h-7" />;
                            const isStart = isSameDay(day, stagedStart);
                            const isEnd = isSameDay(day, stagedEnd);
                            const hasRange = !!stagedStart && !!stagedEnd;
                            const isInRange = hasRange && day > (stagedStart as Date) && day < (stagedEnd as Date);
                            const isRowStart = idx % 7 === 0;
                            const isRowEnd = idx % 7 === 6;
                            const isPastMax = effectiveMaxDate ? day > effectiveMaxDate : false;
                            const isBeforeMin = minDate ? day < minDate : false;
                            const isDisabled = isPastMax || isBeforeMin;
                            const bandLeft = isEnd && !isStart ? 'left-0 right-1/2' : (isInRange || isStart) ? 'inset-x-0' : '';
                            const bandRight = isStart && !isEnd && hasRange ? 'left-1/2 right-0' : '';

                            return (
                                <div key={idx} className="relative h-7 flex items-center justify-center">
                                    {(isInRange || (isStart && hasRange) || (isEnd && !isStart)) && (
                                        <div
                                            className={`absolute inset-y-0 ${bandRight || bandLeft} ${isRowStart ? 'rounded-l-full' : ''} ${isRowEnd ? 'rounded-r-full' : ''}`}
                                            style={{ backgroundColor: `${accentColor}40` }}
                                        />
                                    )}
                                    <button
                                        type="button"
                                        disabled={isDisabled}
                                        onClick={() => handleDayClick(day)}
                                        className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full text-[11px] font-bold transition-all cursor-pointer
                                            ${isStart || isEnd
                                                ? 'text-white shadow-sm'
                                                : isDisabled
                                                    ? 'text-slate-300 cursor-not-allowed'
                                                    : isInRange
                                                        ? 'hover:bg-white/40'
                                                        : 'text-slate-600 hover:bg-slate-100'
                                            }`}
                                        style={isStart || isEnd ? { backgroundColor: accentColor } : undefined}
                                    >
                                        {day.getDate()}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer Actions */}
                    <div className="mt-2 flex items-center justify-between pt-2 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="text-xs font-bold hover:opacity-70 transition-opacity px-1.5 py-0.5 cursor-pointer"
                            style={{ color: accentColor }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleApply}
                            disabled={!stagedStart}
                            className="px-3.5 py-1 text-xs font-bold text-white rounded-xl shadow-xs disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
                            style={{ backgroundColor: accentColor }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;
